import React, { useState, useEffect } from 'react';
import { Button, Card, Timeline, message, Spin } from 'antd';
import { TrophyOutlined, FireOutlined, ClockCircleOutlined, UserOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAccount } from 'wagmi';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

import { useStaking } from '../hooks/useStaking';
import { Player, PlayersData, DayKey } from '../types/player';
import { api } from '../api/api';
import '../styles/global-background.css';
import '../styles/BattlePredictionPage.css';

// 活动时间配置
const eventDays = [
  {
    day: 1,
    date: '2025-05-27',
    status: 'completed', // upcoming, active, completed
    title: '第一天：初战告捷'
  },
  {
    day: 2,
    date: '2025-05-29',
    status: 'active',
    title: '第二天：巅峰对决'
  },
  {
    day: 3,
    date: '2025-05-31',
    status: 'upcoming',
    title: '第三天：终极决战'
  }
];

const BattlePredictionPage: React.FC = () => {
  const [language, setLanguage] = useState<'en' | 'zh'>('zh');
  const [currentDay, setCurrentDay] = useState(1);
  const [selectedPlayer, setSelectedPlayer] = useState<number | null>(null);
  const [userPredictions, setUserPredictions] = useState<{ [key: number]: number }>({});
  const [playersData, setPlayersData] = useState<PlayersData | null>(null);
  const [loading, setLoading] = useState(true);
  // 记录用户下注的选手信息，确保不会因为JSON数据变动而丢失
  const [stakedPlayerInfo, setStakedPlayerInfo] = useState<{
    playerId: number;
    playerName: string;
    playerAvatar: string;
    stakeAmount: string;
    stakeTime: string;
  } | null>(null);

  // 当日下注统计数据
  const [dailyStats, setDailyStats] = useState<any>(null);
  const [isLoadingStats, setIsLoadingStats] = useState(false);

  const navigate = useNavigate();
  const { address, isConnected } = useAccount();

  // 使用质押合约hook
  const {
    stakeAmount,
    stakingEnabled,
    userStake,
    userUnlocked,
    tokenAddress,
    nctBalance,
    allowance,
    isLoading,
    isStaking,
    isApproving,
    isWithdrawing,
    needsApproval,
    hasEnoughBalance,
    canStake,
    approve,
    stake,
    stakeWithApproval,
    withdraw,
    refetchAll,
    approveHash,
    stakeHash,
    withdrawHash,
    requiredAmount
  } = useStaking();

  // 从public目录加载选手数据
  useEffect(() => {
    const loadPlayersData = async () => {
      try {
        setLoading(true);
        const response = await fetch('/data/players.json');
        if (!response.ok) {
          throw new Error('Failed to load players data');
        }
        const data = await response.json();
        setPlayersData(data as PlayersData);
      } catch (error) {
        console.error('Error loading players data:', error);
        message.error('加载选手数据失败');
      } finally {
        setLoading(false);
      }
    };

    loadPlayersData();
  }, []);

  // 根据当前天数获取选手数据
  const getCurrentDayPlayers = (): Player[] => {
    if (!playersData) return [];
    const dayKey = `day${currentDay}` as DayKey;
    return playersData[dayKey] || [];
  };

  const currentPlayers = getCurrentDayPlayers();

  // 模拟获取用户已有的预测
  useEffect(() => {
    // 这里后续会替换为真实的合约调用
    const mockUserPredictions = {
      // 1: 3, // 第一天预测了选手3
    };
    setUserPredictions(mockUserPredictions);
  }, [address]);

  // 从本地存储加载下注信息
  useEffect(() => {
    if (address) {
      const savedStakedInfo = localStorage.getItem(`stakedPlayerInfo_${address}`);
      if (savedStakedInfo) {
        try {
          const parsedInfo = JSON.parse(savedStakedInfo);
          setStakedPlayerInfo(parsedInfo);
        } catch (error) {
          console.error('解析本地存储的下注信息失败:', error);
        }
      }
    }
  }, [address]);

  // 保存下注信息到本地存储
  useEffect(() => {
    if (address && stakedPlayerInfo) {
      localStorage.setItem(`stakedPlayerInfo_${address}`, JSON.stringify(stakedPlayerInfo));
    }
  }, [address, stakedPlayerInfo]);

  // 获取用户下注历史（从后端同步数据）
  useEffect(() => {
    if (address && userStake && !isLoading) {
      fetchUserStakeHistory();
    }
  }, [address, userStake, currentDay, isLoading]);

  // 获取当日下注统计
  useEffect(() => {
    fetchDailyStats();
  }, [currentDay]);

  // 监听授权完成事件，自动执行质押
  useEffect(() => {
    const handleApprovalCompleted = async () => {
      if (selectedPlayer && userStake === '0') {
        try {
          const playerData = currentPlayers.find(p => p.id === selectedPlayer);
          if (playerData) {
            message.info(`正在为选手 ${playerData.name} 执行质押...`);
            await stake();

            // 移除这里的数据保存逻辑，改为在质押真正成功后保存
            // 记录下注信息
            // const stakeInfo = {
            //   playerId: selectedPlayer,
            //   playerName: playerData.name,
            //   playerAvatar: playerData.avatar,
            //   stakeAmount: stakeAmount,
            //   stakeTime: new Date().toLocaleString('zh-CN')
            // };

            // setStakedPlayerInfo(stakeInfo);

            // 发送到后端数据库
            // await saveStakeToDatabase(stakeInfo);
          }
        } catch (error: any) {
          console.error('自动质押失败:', error);
          message.error(`质押失败: ${error.message || '未知错误'}`);
        }
      }
    };

    window.addEventListener('approvalCompleted', handleApprovalCompleted);

    return () => {
      window.removeEventListener('approvalCompleted', handleApprovalCompleted);
    };
  }, [selectedPlayer, userStake, currentPlayers, stake, stakeAmount, address, currentDay]);

  // 监听质押真正成功事件，保存数据到数据库
  useEffect(() => {
    const handleStakeCompleted = async (event: any) => {
      if (selectedPlayer) {
        try {
          const playerData = currentPlayers.find(p => p.id === selectedPlayer);
          if (playerData) {
            // 记录下注信息
            const stakeInfo = {
              playerId: selectedPlayer,
              playerName: playerData.name,
              playerAvatar: playerData.avatar,
              stakeAmount: stakeAmount,
              stakeTime: new Date().toLocaleString('zh-CN')
            };

            setStakedPlayerInfo(stakeInfo);

            // 发送到后端数据库
            await saveStakeToDatabase(stakeInfo);

            message.success(`成功为选手 ${playerData.name} 完成质押！`);
          }
        } catch (error: any) {
          console.error('保存质押信息失败:', error);
          message.error(`保存质押信息失败: ${error.message || '未知错误'}`);
        }
      }
    };

    window.addEventListener('stakeCompleted', handleStakeCompleted);

    return () => {
      window.removeEventListener('stakeCompleted', handleStakeCompleted);
    };
  }, [selectedPlayer, currentPlayers, stakeAmount, address, currentDay]);

  // 获取当日下注统计
  const fetchDailyStats = async () => {
    try {
      setIsLoadingStats(true);
      const response = await api.get(`/cryptokill/battle-prediction/stats?day=${currentDay}`);

      if (response.data.success) {
        setDailyStats(response.data.data);
      } else {
        console.error('❌ 获取当日统计失败:', response.data.message);
        setDailyStats(null);
      }
    } catch (error: any) {
      console.error('🚫 获取当日统计失败:', error);
      setDailyStats(null);
    } finally {
      setIsLoadingStats(false);
    }
  };

  // 从后端获取用户的下注历史
  const fetchUserStakeHistory = async () => {
    if (!address) return;

    try {
      const response = await api.get(`/cryptokill/battle-prediction/stakes/${address}`);

      if (response.data.success && response.data.data) {
        const userStakes = response.data.data;

        // 查找当前天数的下注记录
        const currentDayStake = userStakes.find((stake: any) =>
          stake.day === currentDay && stake.status === 'completed'
        );

        if (currentDayStake && userStake !== '0') {
          // 如果后端有记录且链上也有质押，恢复下注信息
          console.log('🔄 恢复下注信息:', currentDayStake);
          setStakedPlayerInfo({
            playerId: currentDayStake.playerId,
            playerName: currentDayStake.playerName,
            playerAvatar: currentDayStake.playerAvatar,
            stakeAmount: currentDayStake.stakeAmount,
            stakeTime: currentDayStake.stakeTime
          });
        }
      }
    } catch (error: any) {
      console.error('🚫 获取用户下注历史失败:', error);
    }
  };

  const handlePlayerSelect = (playerId: number) => {
    if (userPredictions[currentDay]) {
      message.warning('您已经为今天进行了预测！');
      return;
    }

    if (userStake !== '0') {
      message.warning('您已经参与过质押，无法重复质押');
      return;
    }

    // 只是选中玩家，不直接触发质押
    setSelectedPlayer(playerId);
    message.info(`已选择选手：${currentPlayers.find(p => p.id === playerId)?.name}`);
  };

  const handleStakeClick = async () => {
    if (!isConnected) {
      message.error('请先连接钱包');
      return;
    }

    if (!stakingEnabled) {
      message.warning('质押功能暂时关闭！');
      return;
    }

    if (!hasEnoughBalance) {
      message.error(`NCT代币余额不足，需要至少${requiredAmount} NCT代币才能参与质押`);
      return;
    }

    if (!selectedPlayer) {
      message.error('请先选择要预测的选手');
      return;
    }

    try {
      const playerData = currentPlayers.find(p => p.id === selectedPlayer);
      if (!playerData) {
        message.error('选手信息不存在');
        return;
      }

      if (needsApproval) {
        message.info(`正在为选手 ${playerData.name} 进行授权...`);
        await approve();
      } else {
        message.info(`正在为选手 ${playerData.name} 进行质押...`);
        await stake();

        // 移除这里的数据保存逻辑，改为在质押真正成功后通过事件监听器保存
        // 记录下注信息
        // const stakeInfo = {
        //   playerId: selectedPlayer,
        //   playerName: playerData.name,
        //   playerAvatar: playerData.avatar,
        //   stakeAmount: stakeAmount,
        //   stakeTime: new Date().toLocaleString('zh-CN')
        // };

        // setStakedPlayerInfo(stakeInfo);

        // 发送到后端数据库
        // await saveStakeToDatabase(stakeInfo);
      }
    } catch (error: any) {
      console.error('质押失败:', error);
      message.error(`质押失败: ${error.message || '未知错误'}`);
    }
  };

  const getTimelineColor = (status: string) => {
    switch (status) {
      case 'completed': return '#00ff00';
      case 'active': return '#9d00ff';
      default: return 'rgba(255, 255, 255, 0.4)';
    }
  };

  const getTimelineIcon = (status: string) => {
    switch (status) {
      case 'completed': return <TrophyOutlined />;
      case 'active': return <FireOutlined />;
      default: return <ClockCircleOutlined />;
    }
  };

  // 格式化质押金额显示
  const formatStakeAmount = (amount: string) => {
    const num = parseFloat(amount);
    if (num === 0) return '0';
    return num.toLocaleString('en-US', { maximumFractionDigits: 0 });
  };

  // 根据类别获取标签样式
  const getCategoryTagStyle = (category: string) => {
    const baseStyle = {
      fontSize: '11px',
      color: 'white',
      fontFamily: 'var(--button-font)',
      fontWeight: 700,
      textTransform: 'uppercase' as const,
      letterSpacing: '0.5px',
      padding: '6px 12px',
      borderRadius: '20px',
      border: '2px solid',
      marginBottom: '8px',
      textShadow: '0 0 8px rgba(255, 255, 255, 0.8)',
      position: 'relative' as const,
      overflow: 'hidden' as const,
      animation: 'tagGlow 2s ease-in-out infinite alternate',
    };

    switch (category) {
      case 'Game':
        return {
          ...baseStyle,
          background: 'linear-gradient(135deg, #ff6b35, #f7931e)',
          borderColor: 'rgba(255, 107, 53, 0.8)',
          boxShadow: '0 0 15px rgba(255, 107, 53, 0.6), 0 0 30px rgba(247, 147, 30, 0.4)',
        };
      case '社区':
        return {
          ...baseStyle,
          background: 'linear-gradient(135deg, #4ecdc4, #44a08d)',
          borderColor: 'rgba(78, 205, 196, 0.8)',
          boxShadow: '0 0 15px rgba(78, 205, 196, 0.6), 0 0 30px rgba(68, 160, 141, 0.4)',
        };
      case '主播':
        return {
          ...baseStyle,
          background: 'linear-gradient(135deg, #667eea, #764ba2)',
          borderColor: 'rgba(102, 126, 234, 0.8)',
          boxShadow: '0 0 15px rgba(102, 126, 234, 0.6), 0 0 30px rgba(118, 75, 162, 0.4)',
        };
      case '撸毛，科学家':
        return {
          ...baseStyle,
          background: 'linear-gradient(135deg, #f093fb, #f5576c)',
          borderColor: 'rgba(240, 147, 251, 0.8)',
          boxShadow: '0 0 15px rgba(240, 147, 251, 0.6), 0 0 30px rgba(245, 87, 108, 0.4)',
        };
      case '整活':
        return {
          ...baseStyle,
          background: 'linear-gradient(135deg, #ffecd2, #fcb69f)',
          borderColor: 'rgba(255, 236, 210, 0.8)',
          color: '#333',
          textShadow: '0 0 8px rgba(0, 0, 0, 0.3)',
          boxShadow: '0 0 15px rgba(255, 236, 210, 0.6), 0 0 30px rgba(252, 182, 159, 0.4)',
        };
      case '美女':
        return {
          ...baseStyle,
          background: 'linear-gradient(135deg, #ff9a9e, #fecfef)',
          borderColor: 'rgba(255, 154, 158, 0.8)',
          boxShadow: '0 0 15px rgba(255, 154, 158, 0.6), 0 0 30px rgba(254, 207, 239, 0.4)',
        };
      default:
        return {
          ...baseStyle,
          background: 'linear-gradient(135deg, var(--neon-pink), var(--nova-purple))',
          borderColor: 'rgba(255, 0, 255, 0.6)',
          boxShadow: '0 0 15px rgba(255, 0, 255, 0.5), 0 0 30px rgba(157, 0, 255, 0.3)',
        };
    }
  };

  // 发送下注信息到后端数据库
  const saveStakeToDatabase = async (stakeInfo: {
    playerId: number;
    playerName: string;
    playerAvatar: string;
    stakeAmount: string;
    stakeTime: string;
  }) => {
    try {
      const stakeData = {
        walletAddress: address,
        playerId: stakeInfo.playerId,
        playerName: stakeInfo.playerName,
        playerAvatar: stakeInfo.playerAvatar,
        stakeAmount: stakeInfo.stakeAmount,
        stakeTime: stakeInfo.stakeTime,
        day: currentDay,
        transactionHash: stakeHash || '', // 使用实际的质押交易哈希
        approveTransactionHash: approveHash || '', // 授权交易哈希
        blockNumber: '', // 可以在后续添加区块号
        status: 'completed'
      };

      console.log('📤 发送下注信息到后端:', stakeData);
      const response = await api.post('/cryptokill/battle-prediction/stakes', stakeData);

      if (response.data.success) {
        message.success('下注信息已记录到数据库');
        // 刷新当日统计
        fetchDailyStats();
      } else {
        message.warning('下注信息记录失败，但质押已成功');
      }
    } catch (error: any) {
      console.error('🚫 发送下注信息到后端失败:', error);
      message.warning('下注信息记录失败，但质押已成功');
    }
  };

  // 通知后端用户已提取质押
  const notifyWithdrawalToDatabase = async () => {
    try {
      const withdrawalData = {
        walletAddress: address,
        withdrawalTime: new Date().toLocaleString('zh-CN'),
        transactionHash: withdrawHash || '', // 使用实际的提取交易哈希
        status: 'withdrawn'
      };

      console.log('📤 发送提取信息到后端:', withdrawalData);
      const response = await api.post('/cryptokill/battle-prediction/withdrawals', withdrawalData);

      if (response.data.success) {
        console.log('✅ 提取信息已成功保存到数据库:', response.data);
        message.success('提取信息已记录到数据库');
      } else {
        console.error('❌ 保存提取信息失败:', response.data.message);
      }
    } catch (error: any) {
      console.error('🚫 发送提取信息到后端失败:', error);
    }
  };

  return (
    <div className="nova-container">
      <div className="nova-grid"></div>

      <Navbar language={language} />

      <div className="content">
        <div className="title-section">
          <h1 className="nova-title">
            <TrophyOutlined style={{ marginRight: '12px', color: '#ff00ff' }} />
            {language === 'en' ? 'Crypto Battle Royale' : '币圈大乱斗主播赛'}
          </h1>
          <p className="hero-subtitle">
            {language === 'en'
              ? 'Predict daily champions and win amazing rewards!'
              : '预测每日冠军，赢取丰厚奖励！'}
          </p>

          {/* 活动说明卡片 */}
          <Card
            className="event-info-card"
            style={{
              margin: '20px auto',
              maxWidth: '800px'
            }}
          >
            <div style={{ textAlign: 'center' }}>
              <h3 style={{ marginBottom: '16px' }}>
                🎯 {language === 'en' ? 'How to Participate' : '参与方式'}
              </h3>
              <p style={{
                fontSize: '1.2rem',
                fontFamily: 'var(--text-font)',
                color: 'rgba(255, 255, 255, 0.9)',
                lineHeight: '1.6',
                margin: '0 auto',
                maxWidth: '600px',
                fontWeight: 500,
                textShadow: '0 0 8px rgba(255, 255, 255, 0.3)'
              }}>
                在活动期间质押NClab代币预测你认为的冠军选手获得丰厚奖励！
              </p>

              {/* 显示质押状态信息 */}
              <div style={{
                marginTop: '1rem',
                fontSize: '0.9rem',
                color: 'rgba(255, 255, 255, 0.7)',
                fontFamily: 'var(--text-font)'
              }}>
                <div>当前状态: {stakingEnabled ? '✅ 可下注' : '❌ 已封盘'}</div>
                {userStake !== '0' && (
                  <div>您已质押: {formatStakeAmount(userStake)} NClab</div>
                )}
              </div>
            </div>
          </Card>

          {/* 当日下注统计卡片 */}
          <Card
            className="daily-stats-card"
            style={{
              margin: '20px auto',
              maxWidth: '800px',
              background: 'rgba(0, 255, 255, 0.1)',
              border: '1px solid rgba(0, 255, 255, 0.3)'
            }}
            loading={isLoadingStats}
            extra={
              <Button
                size="small"
                onClick={fetchDailyStats}
                loading={isLoadingStats}
                style={{
                  fontSize: '12px',
                  height: '28px',
                  background: 'rgba(0, 255, 255, 0.1)',
                  border: '1px solid rgba(0, 255, 255, 0.3)',
                  color: '#00ffff'
                }}
              >
                🔄 刷新
              </Button>
            }
          >
            <div style={{ textAlign: 'center' }}>
              <h3 style={{
                marginBottom: '20px',
                color: '#00ffff',
                fontFamily: 'var(--text-font)',
                textShadow: '0 0 8px rgba(0, 255, 255, 0.5)'
              }}>
                📊 第{currentDay}天下注统计
              </h3>

              {dailyStats ? (
                <>
                  {/* 总体统计 */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                    gap: '16px',
                    marginBottom: '24px'
                  }}>
                    <div style={{
                      padding: '12px',
                      background: 'rgba(255, 215, 0, 0.1)',
                      border: '1px solid rgba(255, 215, 0, 0.3)',
                      borderRadius: '8px'
                    }}>
                      <div style={{ fontSize: '14px', color: '#ffd700', marginBottom: '4px' }}>
                        总下注人数
                      </div>
                      <div className="stat-number" style={{ fontSize: '20px', fontWeight: 'bold', color: '#fff' }}>
                        {dailyStats.totalStakers}
                      </div>
                    </div>

                    <div style={{
                      padding: '12px',
                      background: 'rgba(0, 255, 0, 0.1)',
                      border: '1px solid rgba(0, 255, 0, 0.3)',
                      borderRadius: '8px'
                    }}>
                      <div style={{ fontSize: '14px', color: '#00ff00', marginBottom: '4px' }}>
                        总下注金额
                      </div>
                      <div className="stat-number" style={{ fontSize: '20px', fontWeight: 'bold', color: '#fff' }}>
                        {formatStakeAmount(dailyStats.totalStakeAmount.toString())} NCT
                      </div>
                    </div>

                    <div style={{
                      padding: '12px',
                      background: 'rgba(255, 0, 255, 0.1)',
                      border: '1px solid rgba(255, 0, 255, 0.3)',
                      borderRadius: '8px'
                    }}>
                      <div style={{ fontSize: '14px', color: '#ff00ff', marginBottom: '4px' }}>
                        参与选手数
                      </div>
                      <div className="stat-number" style={{ fontSize: '20px', fontWeight: 'bold', color: '#fff' }}>
                        {dailyStats.uniquePlayers}
                      </div>
                    </div>
                  </div>

                  {/* 选手下注排行 */}
                  {dailyStats.playerStats && dailyStats.playerStats.length > 0 && (
                    <div>
                      <h4 style={{
                        color: '#00ffff',
                        marginBottom: '16px',
                        fontFamily: 'var(--text-font)'
                      }}>
                        🏆 选手下注排行榜
                      </h4>
                      <div style={{
                        display: 'grid',
                        gap: '12px',
                        maxHeight: '300px',
                        overflow: 'auto'
                      }}>
                        {dailyStats.playerStats
                          .sort((a: any, b: any) => b.totalStakeAmount - a.totalStakeAmount)
                          .map((playerStat: any, index: number) => (
                            <div
                              key={playerStat.playerId}
                              className={`ranking-item ${index === 0 ? 'gold-rank' : ''}`}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                padding: '12px',
                                background: index === 0 ? 'rgba(255, 215, 0, 0.15)' :
                                  index === 1 ? 'rgba(192, 192, 192, 0.15)' :
                                    index === 2 ? 'rgba(205, 127, 50, 0.15)' :
                                      'rgba(255, 255, 255, 0.05)',
                                border: `1px solid ${index === 0 ? 'rgba(255, 215, 0, 0.4)' :
                                    index === 1 ? 'rgba(192, 192, 192, 0.4)' :
                                      index === 2 ? 'rgba(205, 127, 50, 0.4)' :
                                        'rgba(255, 255, 255, 0.1)'
                                  }`,
                                borderRadius: '8px',
                                gap: '12px'
                              }}
                            >
                              {/* 排名 */}
                              <div className={index < 3 ? 'rank-badge' : ''} style={{
                                width: '32px',
                                height: '32px',
                                borderRadius: '50%',
                                background: index === 0 ? 'linear-gradient(135deg, #ffd700, #ffed4e)' :
                                  index === 1 ? 'linear-gradient(135deg, #c0c0c0, #e8e8e8)' :
                                    index === 2 ? 'linear-gradient(135deg, #cd7f32, #daa520)' :
                                      'linear-gradient(135deg, #666, #888)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '14px',
                                fontWeight: 'bold',
                                color: index < 3 ? '#000' : '#fff'
                              }}>
                                {index === 0 ? '👑' : index === 1 ? '🥈' : index === 2 ? '🥉' : index + 1}
                              </div>

                              {/* 选手头像 */}
                              <img
                                src={playerStat.playerAvatar}
                                alt={playerStat.playerName}
                                style={{
                                  width: '40px',
                                  height: '40px',
                                  borderRadius: '50%',
                                  objectFit: 'cover',
                                  border: '2px solid rgba(0, 255, 255, 0.3)'
                                }}
                              />

                              {/* 选手信息 */}
                              <div style={{ flex: 1, textAlign: 'left' }}>
                                <div style={{
                                  fontSize: '16px',
                                  fontWeight: 'bold',
                                  color: '#fff',
                                  fontFamily: 'var(--text-font)'
                                }}>
                                  {playerStat.playerName}
                                </div>
                                <div style={{
                                  fontSize: '12px',
                                  color: 'rgba(255, 255, 255, 0.7)',
                                  fontFamily: 'var(--text-font)'
                                }}>
                                  {playerStat.stakeCount} 人下注
                                </div>
                              </div>

                              {/* 下注金额 */}
                              <div style={{ textAlign: 'right' }}>
                                <div style={{
                                  fontSize: '16px',
                                  fontWeight: 'bold',
                                  color: '#00ff00',
                                  fontFamily: 'var(--text-font)'
                                }}>
                                  {formatStakeAmount(playerStat.totalStakeAmount.toString())} NCT
                                </div>
                                <div style={{
                                  fontSize: '12px',
                                  color: 'rgba(255, 255, 255, 0.7)',
                                  fontFamily: 'var(--text-font)'
                                }}>
                                  {((playerStat.totalStakeAmount / dailyStats.totalStakeAmount) * 100).toFixed(1)}%
                                </div>
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div style={{
                  color: 'rgba(255, 255, 255, 0.6)',
                  fontFamily: 'var(--text-font)',
                  padding: '20px'
                }}>
                  {isLoadingStats ? '正在加载统计数据...' : '暂无下注数据'}
                </div>
              )}
            </div>
          </Card>

          {/* 质押状态面板 */}
          {isConnected && (
            <Card
              className="staking-status-card"
              style={{
                margin: '20px auto',
                maxWidth: '800px',
                background: 'rgba(157, 0, 255, 0.1)',
                border: '1px solid rgba(157, 0, 255, 0.3)'
              }}
            >
              <div style={{ textAlign: 'center' }}>
                <h3 style={{
                  marginBottom: '20px',
                  color: '#ff00ff',
                  fontFamily: 'var(--text-font)'
                }}>
                  💰 我的质押状态
                </h3>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '16px',
                  marginBottom: '20px'
                }}>
                  {/* NCT余额 */}
                  <div style={{
                    padding: '12px',
                    background: 'rgba(0, 255, 255, 0.1)',
                    border: '1px solid rgba(0, 255, 255, 0.3)',
                    borderRadius: '8px'
                  }}>
                    <div style={{ fontSize: '14px', color: '#00ffff', marginBottom: '4px' }}>
                      NCT 余额
                    </div>
                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#fff' }}>
                      {formatStakeAmount(nctBalance)} NCT
                    </div>
                  </div>

                  {/* 质押状态 */}
                  <div style={{
                    padding: '12px',
                    background: userStake !== '0' ? 'rgba(0, 255, 0, 0.1)' : 'rgba(128, 128, 128, 0.1)',
                    border: `1px solid ${userStake !== '0' ? 'rgba(0, 255, 0, 0.3)' : 'rgba(128, 128, 128, 0.3)'}`,
                    borderRadius: '8px'
                  }}>
                    <div style={{ fontSize: '14px', color: userStake !== '0' ? '#00ff00' : '#888', marginBottom: '4px' }}>
                      已质押
                    </div>
                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#fff' }}>
                      {formatStakeAmount(userStake)} NClab
                    </div>
                  </div>
                </div>

                {/* 下注选手信息 */}
                {stakedPlayerInfo && userStake !== '0' && (
                  <div style={{
                    padding: '16px',
                    background: 'rgba(255, 215, 0, 0.1)',
                    border: '1px solid rgba(255, 215, 0, 0.3)',
                    borderRadius: '12px',
                    marginBottom: '20px'
                  }}>
                    <div style={{
                      fontSize: '14px',
                      color: '#ffd700',
                      marginBottom: '12px',
                      fontWeight: 'bold',
                      textAlign: 'center'
                    }}>
                      🎯 您的下注信息
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>
                      <img
                        src={stakedPlayerInfo.playerAvatar}
                        alt={stakedPlayerInfo.playerName}
                        style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          objectFit: 'cover',
                          border: '2px solid rgba(255, 215, 0, 0.5)'
                        }}
                      />
                      <div>
                        <div style={{
                          fontSize: '16px',
                          fontWeight: 'bold',
                          color: '#fff',
                          fontFamily: 'var(--text-font)'
                        }}>
                          {stakedPlayerInfo.playerName}
                        </div>
                        <div style={{
                          fontSize: '12px',
                          color: 'rgba(255, 255, 255, 0.7)',
                          fontFamily: 'var(--text-font)'
                        }}>
                          质押: {stakedPlayerInfo.stakeAmount} NCT
                        </div>
                        <div style={{
                          fontSize: '10px',
                          color: 'rgba(255, 255, 255, 0.5)',
                          fontFamily: 'var(--text-font)'
                        }}>
                          时间: {stakedPlayerInfo.stakeTime}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* 操作按钮区域 */}
                {
                  <div style={{ marginBottom: '16px' }}>
                    <Button
                      type="primary"
                      size="large"
                      disabled={!stakingEnabled || !userUnlocked || userStake === '0'}
                      onClick={async () => {
                        try {
                          await withdraw();
                          // 通知后端用户已提取
                          await notifyWithdrawalToDatabase();
                          // 提取成功后清除下注信息
                          if (address) {
                            setStakedPlayerInfo(null);
                            localStorage.removeItem(`stakedPlayerInfo_${address}`);
                          }
                        } catch (error: any) {
                          console.error('提取操作失败:', error);
                        }
                      }}
                      style={{
                        background: stakingEnabled && userUnlocked && userStake !== '0'
                          ? 'linear-gradient(135deg, #ff4444, #cc0000)'
                          : 'linear-gradient(135deg, #666, #444)',
                        border: 'none',
                        fontFamily: 'var(--button-font)',
                        fontWeight: 'bold',
                        textShadow: '0 0 8px rgba(255, 255, 255, 0.8)',
                        boxShadow: stakingEnabled && userUnlocked && userStake !== '0'
                          ? '0 0 15px rgba(255, 68, 68, 0.5)'
                          : '0 0 15px rgba(102, 102, 102, 0.3)',
                        cursor: stakingEnabled && userUnlocked && userStake !== '0' ? 'pointer' : 'not-allowed'
                      }}
                    >
                      {userStake === '0' ? '暂无质押' :
                        !stakingEnabled ? '🔒 质押功能已关闭' :
                          !userUnlocked ? '🔒 质押已锁定' :
                            '提取质押'}
                    </Button>

                    {/* 手动刷新按钮 */}
                    <Button
                      size="small"
                      onClick={refetchAll}
                      style={{
                        marginTop: '8px',
                        fontSize: '12px',
                        height: '28px',
                        background: 'rgba(0, 255, 255, 0.1)',
                        border: '1px solid rgba(0, 255, 255, 0.3)',
                        color: '#00ffff'
                      }}
                    >
                      🔄 刷新数据
                    </Button>
                  </div>
                }

                {/* 状态提示信息 */}
                <div style={{
                  fontSize: '12px',
                  color: 'rgba(255, 255, 255, 0.6)',
                  fontFamily: 'var(--text-font)'
                }}>
                  {userStake === '0' ? (
                    <div style={{ color: '#00ffff' }}>
                      🎯 选择一位选手后点击质押按钮开始质押流程
                    </div>
                  ) : !stakingEnabled ? (
                    <div style={{ color: '#ff4444' }}>
                      🔒 质押功能已关闭，无法提取
                    </div>
                  ) : !userUnlocked ? (
                    <div style={{ color: '#888' }}>
                      🔒 质押已锁定，等待管理员解锁后可提取
                    </div>
                  ) : (
                    <div style={{ color: '#00ff00' }}>
                      ✅ 质押已解锁，可以提取
                    </div>
                  )}

                  {!hasEnoughBalance && userStake === '0' && (
                    <div style={{ color: '#ff4444', marginTop: '8px' }}>
                      ⚠️ NCT余额不足，需要至少 {requiredAmount} NCT 才能参与质押
                    </div>
                  )}

                  {!stakingEnabled && (
                    <div style={{ color: '#ff4444', marginTop: '8px' }}>
                      ⚠️ 质押功能已关闭
                    </div>
                  )}
                </div>
              </div>
            </Card>
          )}
        </div>

        {/* 时间轴 */}
        <div className="timeline-section">
          <h2>
            📅 {language === 'en' ? 'Event Timeline' : '活动时间轴'}
          </h2>
          <Timeline
            mode="alternate"
            style={{ padding: '20px 0' }}
            items={eventDays.map(day => ({
              color: getTimelineColor(day.status),
              dot: getTimelineIcon(day.status),
              children: (
                <div
                  style={{
                    textAlign: 'center',
                    cursor: 'pointer',
                    padding: '10px',
                    borderRadius: '8px',
                    background: currentDay === day.day ? 'rgba(157, 0, 255, 0.2)' : 'transparent',
                    border: currentDay === day.day ? '1px solid rgba(157, 0, 255, 0.5)' : '1px solid transparent'
                  }}
                  onClick={() => setCurrentDay(day.day)}
                >
                  <div style={{ fontWeight: 'bold', color: '#fff', fontFamily: 'var(--text-font)' }}>
                    {day.title}
                  </div>
                  <div style={{ fontSize: '12px', color: '#ccc', fontFamily: 'var(--text-font)' }}>
                    {day.date}
                  </div>
                  <div style={{
                    fontSize: '10px',
                    color: day.status === 'active' ? '#00ff00' : day.status === 'completed' ? '#00ff00' : '#999',
                    fontFamily: 'var(--text-font)',
                    textShadow: day.status === 'active' || day.status === 'completed' ? '0 0 8px #00ff00' : 'none'
                  }}>
                    {day.status === 'completed' ? '已结束' : day.status === 'active' ? '进行中' : '未开始'}
                  </div>
                </div>
              )
            }))}
          />
        </div>

        {/* 选手选择区域 */}
        <div className="players-section">
          <h2>
            🎮 第{currentDay}天参赛选手 ({currentPlayers.length}位)
          </h2>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '3rem' }}>
              <Spin size="large" />
              <div style={{
                marginTop: '1rem',
                color: 'rgba(255, 255, 255, 0.8)',
                fontFamily: 'var(--text-font)'
              }}>
                加载选手数据中...
              </div>
            </div>
          ) : (
            <>
              {userPredictions[currentDay] && (
                <div style={{
                  textAlign: 'center',
                  marginBottom: '20px',
                  padding: '12px',
                  background: 'rgba(0, 255, 0, 0.1)',
                  border: '1px solid rgba(0, 255, 0, 0.4)',
                  borderRadius: '12px',
                  fontFamily: 'var(--text-font)',
                  fontWeight: 600,
                  color: '#00ff00',
                  textShadow: '0 0 8px #00ff00',
                  boxShadow: '0 0 15px rgba(0, 255, 0, 0.2)'
                }}>
                  ✅ 您已预测选手：{currentPlayers.find(p => p.id === userPredictions[currentDay])?.name}
                </div>
              )}

              <div className="players-grid">
                {currentPlayers.map(player => (
                  <Card
                    key={player.id}
                    className={`player-card ${selectedPlayer === player.id ? 'selected' : ''} ${userPredictions[currentDay] === player.id ? 'predicted' : ''}`}
                    hoverable={!userPredictions[currentDay] && stakingEnabled}
                    onClick={() => handlePlayerSelect(player.id)}
                    style={{ textAlign: 'center' }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '12px' }}>
                      <img
                        src={player.avatar}
                        alt={player.name}
                        style={{
                          width: '64px',
                          height: '64px',
                          borderRadius: '50%',
                          objectFit: 'cover',
                          border: '2px solid rgba(157, 0, 255, 0.3)',
                          boxShadow: '0 0 10px rgba(157, 0, 255, 0.2)'
                        }}
                      />
                    </div>
                    <h3 style={{ marginBottom: '8px', fontFamily: 'var(--text-font)' }}>
                      {player.name}
                    </h3>
                    <div style={{
                      fontSize: '12px',
                      color: 'rgba(255, 255, 255, 0.6)',
                      fontFamily: 'var(--text-font)',
                      marginBottom: '4px'
                    }}>
                      {player.handle}
                    </div>
                    <div style={{
                      fontSize: '12px',
                      color: 'rgba(255, 255, 255, 0.8)',
                      fontFamily: 'var(--text-font)',
                      marginBottom: '8px',
                      fontWeight: 600
                    }}>
                      👥 {player.followers.toLocaleString()} 粉丝
                    </div>
                    <div
                      className="player-category-tag"
                      style={getCategoryTagStyle(player.category)}
                    >
                      🏷️ {player.category}
                    </div>
                    <div style={{
                      fontSize: '12px',
                      color: 'rgba(255, 255, 255, 0.7)',
                      fontFamily: 'var(--text-font)',
                      marginTop: '8px'
                    }}>
                      当前积分: {player.score}
                    </div>
                    {userPredictions[currentDay] === player.id && (
                      <div style={{
                        color: '#00ff00',
                        fontSize: '12px',
                        marginTop: '8px',
                        fontFamily: 'var(--text-font)',
                        fontWeight: 600,
                        textShadow: '0 0 8px #00ff00'
                      }}>
                        ✅ 已预测
                      </div>
                    )}
                  </Card>
                ))}
              </div>

              {/* 预测按钮 */}
              {!userPredictions[currentDay] && (
                <div style={{ textAlign: 'center', marginTop: '30px' }}>
                  <Button
                    type="primary"
                    size="large"
                    disabled={!selectedPlayer || !isConnected || !stakingEnabled || !hasEnoughBalance || userStake !== '0'}
                    onClick={handleStakeClick}
                    style={{
                      fontFamily: 'var(--button-font)',
                      fontWeight: 700,
                      fontSize: '1rem',
                      textTransform: 'uppercase',
                      letterSpacing: '1px',
                      background: (!selectedPlayer || !isConnected || !stakingEnabled || !hasEnoughBalance || userStake !== '0')
                        ? 'linear-gradient(135deg, #666, #444)'
                        : 'linear-gradient(135deg, var(--nova-purple), var(--neon-pink))',
                      border: 'none',
                      boxShadow: (!selectedPlayer || !isConnected || !stakingEnabled || !hasEnoughBalance || userStake !== '0')
                        ? '0 0 15px rgba(102, 102, 102, 0.3)'
                        : '0 8px 25px rgba(157, 0, 255, 0.4), 0 0 20px rgba(255, 0, 255, 0.3)'
                    }}
                  >
                    {!isConnected
                      ? '请先连接钱包'
                      : userStake !== '0'
                        ? '您已参与质押'
                        : !stakingEnabled
                          ? '质押功能暂时关闭'
                          : !hasEnoughBalance
                            ? `NCT余额不足(需要${requiredAmount})`
                            : !selectedPlayer
                              ? '请选择要预测的选手'
                              : `🚀 质押 ${formatStakeAmount(stakeAmount)} NCT 预测 ${currentPlayers.find(p => p.id === selectedPlayer)?.name}`
                    }
                  </Button>
                </div>
              )}
            </>
          )}
        </div>

        <Footer language={language} setLanguage={setLanguage} />
      </div>
    </div>
  );
};

export default BattlePredictionPage; 